import React from "react";
import { LegalLayout, Section } from "./legal/LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout
      testId="privacy-page"
      eyebrow="Legal"
      title="Privacy"
      accent="Policy."
      lastUpdated="July 5, 2026"
    >
      <p className="text-base text-slate-600 leading-relaxed mb-2">
        This Privacy Policy explains what personal data Nexso collects, why we
        collect it, how we use it, and the choices you have. It applies to the
        Nexso web dashboard at{" "}
        <a href="https://nexso.net" className="text-sky-600 hover:underline">nexso.net</a>,
        the Nexso Android launcher installed on your screens, and the APIs that
        connect them.
      </p>

      <Section id="who" title="1. Who we are">
        <p>
          Nexso (&ldquo;<strong>we</strong>&rdquo;, &ldquo;<strong>us</strong>&rdquo;) is the operator of the Nexso remote
          digital signage platform. For the purposes of the EU General Data
          Protection Regulation (GDPR) and the UK GDPR, Nexso acts as the{" "}
          <strong>data controller</strong> of information you provide to your Nexso
          account, and as a <strong>data processor</strong> for content you display to
          your end viewers.
        </p>
        <p>
          Data-protection questions can be sent to{" "}
          <a href="mailto:privacy@nexso.net" className="text-sky-600 hover:underline">privacy@nexso.net</a>.
        </p>
      </Section>

      <Section id="collected" title="2. What we collect">
        <p>We only collect what we need to operate the service. Specifically:</p>

        <h3 className="text-lg font-semibold mt-4 text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>a. Account &amp; profile data</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li>Email address, password (stored as a bcrypt hash &mdash; never plaintext)</li>
          <li>Display name and business name (optional)</li>
          <li>Plan status, trial dates, promo redemptions, AI credit balance</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>b. Payment data</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li>Stripe Customer ID + Subscription ID</li>
          <li>Invoice records, payment transactions, refund status</li>
          <li><strong>We do NOT store your credit card number.</strong> Card details are handled entirely by Stripe.</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>c. Content you create</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li>Design configurations (text, colours, layout, prices, images you upload)</li>
          <li>Media library files (images, videos, documents you upload)</li>
          <li>Playlists, screen assignments, schedules, brand kit palette</li>
          <li>Support-ticket messages you send us via the help form</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>d. Screen &amp; device telemetry</h3>
        <p>Each paired screen periodically reports the following so we can keep it online and help you troubleshoot:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Anonymous device id (a random UUID stored in the screen&rsquo;s local storage &mdash; not tied to any government identifier)</li>
          <li>OS version, hardware manufacturer &amp; model, screen resolution, user-agent string</li>
          <li>Nexso launcher app version</li>
          <li>Local (LAN) IP address, Wi-Fi SSID, Wi-Fi signal strength &mdash; used for support &amp; ADB access diagnostics</li>
          <li>Public (WAN) IP address &mdash; derived from HTTP proxy headers, used for geo-triage and abuse prevention</li>
          <li>&ldquo;Last seen&rdquo; timestamp for online/offline indicators</li>
        </ul>
        <p><strong>We do NOT collect:</strong> IMEI, MAC address, GPS coordinates, camera feeds, microphone input, ambient audio, or content displayed to end viewers.</p>

        <h3 className="text-lg font-semibold mt-4 text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>e. Usage &amp; log data</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li>Server access logs (IP, timestamp, endpoint, response code)</li>
          <li>Feature usage counters (e.g. AI image generations, template clones) &mdash; aggregate only</li>
          <li>Error/crash reports triggered by the dashboard or the launcher</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>f. Cookies</h3>
        <p>
          Nexso uses <strong>strictly necessary</strong> httpOnly cookies to keep you
          logged in (`access_token`, `refresh_token`) and to remember your session.
          We do not use tracking cookies, third-party ad cookies, or Google
          Analytics. If we add analytics in the future we will update this policy
          and offer an opt-out.
        </p>
      </Section>

      <Section id="how" title="3. How we use your data">
        <p>We use the data described above to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Provide, maintain, and improve the service</li>
          <li>Authenticate you and keep your account secure</li>
          <li>Bill you correctly and issue receipts</li>
          <li>Deliver content to your paired screens in real time</li>
          <li>Respond to your support requests</li>
          <li>Detect abuse, fraud, and violations of our Terms of Service</li>
          <li>Comply with applicable legal obligations</li>
        </ul>
        <p>
          Legal basis under GDPR: performance of the contract with you (Art 6(1)(b)),
          our legitimate interests in running a secure service (Art 6(1)(f)), and
          your consent where required (Art 6(1)(a)).
        </p>
      </Section>

      <Section id="sharing" title="4. Who we share data with">
        <p>
          We do <strong>not</strong> sell your personal data. We share limited data with
          the following sub-processors to operate the service:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Stripe, Inc.</strong> &mdash; payment processing, subscription billing</li>
          <li><strong>Emergent Labs / OpenAI / Google</strong> &mdash; AI image and text generation (only the prompt and any reference image you submit)</li>
          <li><strong>Canva Pty Ltd</strong> &mdash; only if you connect your Canva account via OAuth for design import</li>
          <li><strong>Pexels &amp; Unsplash</strong> &mdash; queried for stock photos when you search their libraries</li>
          <li><strong>Telegram (Telegram FZ-LLC)</strong> &mdash; support message delivery to our on-call team</li>
          <li><strong>MongoDB Inc.</strong> and our cloud infrastructure provider &mdash; hosting and storage</li>
        </ul>
        <p>
          Each sub-processor is bound by contract to protect data at least to the
          standard required by GDPR and our internal policies. We may also disclose
          data if legally compelled to do so by a valid court order or law-enforcement
          request from a jurisdiction we operate in.
        </p>
      </Section>

      <Section id="retention" title="5. How long we keep data">
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Account data:</strong> for the life of the account, plus 30 days after cancellation to allow reactivation</li>
          <li><strong>Content (designs, media):</strong> until you delete it or your account is deleted</li>
          <li><strong>Payment records:</strong> up to 7 years, as required by tax and accounting law</li>
          <li><strong>Server logs:</strong> up to 90 days rolling</li>
          <li><strong>Support tickets:</strong> up to 24 months for continuity of care</li>
        </ul>
      </Section>

      <Section id="rights" title="6. Your rights">
        <p>Depending on your country you have the right to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Access</strong> a copy of the personal data we hold about you</li>
          <li><strong>Correct</strong> inaccurate or outdated data</li>
          <li><strong>Delete</strong> your account and associated data (right to erasure)</li>
          <li><strong>Restrict</strong> or object to certain processing</li>
          <li><strong>Portability</strong> &mdash; export your data in a machine-readable format</li>
          <li><strong>Withdraw consent</strong> at any time where processing is based on consent</li>
          <li><strong>Lodge a complaint</strong> with a data-protection authority</li>
        </ul>
        <p>
          To exercise any of these rights email{" "}
          <a href="mailto:privacy@nexso.net" className="text-sky-600 hover:underline">privacy@nexso.net</a>{" "}
          from the address associated with your account. We&rsquo;ll respond within 30
          days as required by law.
        </p>
        <p>
          <strong>California residents (CCPA/CPRA):</strong> you have equivalent rights to
          know, delete, and opt out of &ldquo;sale&rdquo; or &ldquo;sharing&rdquo; of personal
          information. Nexso does not sell or share personal information as those
          terms are defined in the CCPA.
        </p>
      </Section>

      <Section id="security" title="7. Security">
        <p>
          We protect your data with industry-standard measures: HTTPS/TLS in transit,
          encryption at rest for backups, bcrypt password hashing, httpOnly cookies
          for session tokens, principle-of-least-privilege access for our staff, and
          regular dependency updates. No system is 100% secure &mdash; if we ever
          suffer a breach affecting your data we&rsquo;ll notify you within 72 hours as
          required by GDPR Art 34.
        </p>
      </Section>

      <Section id="children" title="8. Children">
        <p>
          Nexso is intended for business use. We do not knowingly collect personal
          data from children under 16. If you believe a child has provided us data,
          email{" "}
          <a href="mailto:privacy@nexso.net" className="text-sky-600 hover:underline">privacy@nexso.net</a>{" "}
          and we&rsquo;ll delete it promptly.
        </p>
      </Section>

      <Section id="international" title="9. International transfers">
        <p>
          Nexso is a global service. Your data may be processed in countries outside
          your home jurisdiction, including the United States. When we transfer
          personal data out of the EU/UK we rely on the European Commission&rsquo;s
          <strong>Standard Contractual Clauses</strong> or an equivalent lawful transfer
          mechanism.
        </p>
      </Section>

      <Section id="changes" title="10. Changes to this policy">
        <p>
          If we make material changes to this Privacy Policy we will notify you by
          email or via an in-app banner at least 14 days before they take effect.
          The &ldquo;Last updated&rdquo; date at the top of this page reflects the current
          version.
        </p>
      </Section>

      <Section id="contact" title="11. Contact">
        <p>
          For any privacy question, request to exercise your rights, or data-breach
          notification, contact us at:
        </p>
        <p className="text-slate-800">
          <strong>Nexso &mdash; Data Protection</strong><br />
          Email: <a href="mailto:privacy@nexso.net" className="text-sky-600 hover:underline">privacy@nexso.net</a><br />
          General: <a href="mailto:hello@nexso.net" className="text-sky-600 hover:underline">hello@nexso.net</a>
        </p>
      </Section>
    </LegalLayout>
  );
}
